import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasSuperPrivileges } from "@/lib/accountRole";
import { toast } from "@/lib/toast";
import { Loader2, RefreshCw, Search, Ticket, Ban, RotateCcw } from "lucide-react";

const fmtCents = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");
const fmtShort = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function SummaryTile({ label, value, sub, tone, testid }) {
  const valColor = tone === "lime" ? "#cfe86b" : tone === "amber" ? "#E0842F" : undefined;
  return (
    <div className="border border-sniper bg-surface p-5" data-testid={testid}>
      <span className="block font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">{label}</span>
      <div className="font-heading text-4xl font-black mt-1 tabular-nums" style={valColor ? { color: valColor } : undefined} data-testid={`${testid}-value`}>{value}</div>
      {sub ? <p className="text-[11px] text-muted-foreground font-body mt-2 leading-relaxed">{sub}</p> : null}
    </div>
  );
}

function StatusChip({ row }) {
  const s = row.status;
  const map = {
    completed: ["bg-olive/15 text-olive-bright border-olive", "PAID"],
    refunded: ["bg-blaze/15 text-blaze border-blaze", "REFUNDED"],
    pending_payment: ["bg-surface text-muted-foreground border-sniper", "PENDING"],
  };
  const [cls, label] = map[s] || ["bg-surface text-muted-foreground border-sniper", (s || "—").toUpperCase()];
  return <span className={`inline-block font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 border ${cls}`} data-testid={`bp-status-${row.purchase_id}`}>{label}</span>;
}

// Confirm modal shared by Refund + Revoke.
function ActionModal({ mode, row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [revoke, setRevoke] = useState(true);
  const [busy, setBusy] = useState(false);
  const isRefund = mode === "refund";

  const submit = async () => {
    setBusy(true);
    try {
      if (isRefund) {
        const { data } = await api.post(`/admin/buyer-pass/purchases/${row.purchase_id}/refund`, { reason, revoke_access: revoke });
        toast.success(data.refunded_at_stripe ? "Refunded at Stripe" + (data.revoked ? " + access revoked." : ".") : "Marked refunded (no live Stripe charge)" + (data.revoked ? " + access revoked." : "."));
      } else {
        await api.post(`/admin/buyer-pass/purchases/${row.purchase_id}/revoke`, { reason });
        toast.success("Buyer Pass access revoked (no refund).");
      }
      onDone();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" data-testid="bp-action-modal" onClick={onClose}>
      <div className="bg-surface border border-sniper w-full max-w-md p-5 text-foreground" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold uppercase" data-testid="bp-action-title">
          {isRefund ? "Refund Buyer Pass" : "Revoke access"}
        </h3>
        <p className="text-[13px] text-secondary-foreground mt-1">
          {row.username ? `@${row.username}` : row.user_id} · {fmtCents(row.amount_cents)} · {row.purchase_id}
        </p>
        <p className="text-[12px] text-muted-foreground mt-2 leading-relaxed">
          {isRefund
            ? "Refunds the $5 charge to the buyer's card via Stripe. Super-admin only."
            : "Ends this account's Buyer Pass access immediately WITHOUT refunding money."}
        </p>
        {isRefund && (
          <label className="flex items-center gap-2 mt-3 text-[13px] text-foreground cursor-pointer" data-testid="bp-refund-revoke-toggle">
            <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} className="accent-olive" />
            Also revoke the pass access
          </label>
        )}
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, logged to the audit trail)"
          rows={2}
          data-testid="bp-action-reason"
          className="w-full mt-3 bg-background border border-sniper px-3 py-2 text-[13px] text-foreground font-body focus:outline-none focus:border-olive"
        />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} data-testid="bp-action-cancel" className="flex-1 border border-sniper hover:border-olive font-mono uppercase text-xs py-2.5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="bp-action-confirm"
            className={`flex-1 font-mono uppercase text-xs py-2.5 transition-colors disabled:opacity-50 ${isRefund ? "bg-blaze/20 border border-blaze text-blaze hover:bg-blaze/30" : "bg-olive/20 border border-olive text-olive-bright hover:bg-olive/30"}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : isRefund ? "Refund" : "Revoke"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBuyerPasses() {
  const { user } = useAuth();
  const superAdmin = hasSuperPrivileges(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null); // { mode, row }
  const [active, setActive] = useState(null);  // active/expiring overview

  const loadActive = useCallback(async () => {
    try { const { data } = await api.get("/admin/buyer-pass/active"); setActive(data); } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/buyer-pass/purchases", { params: q ? { q } : {} });
      setRows(data.purchases || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not load Buyer Passes.");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); loadActive(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 text-foreground" data-testid="admin-buyer-passes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-3" data-testid="admin-buyer-passes-title">
            <Ticket className="w-7 h-7 text-olive-bright" /> Buyer Passes
          </h2>
          <p className="text-xs font-mono uppercase text-muted-foreground mt-1">One-time $5 · 7-day access purchases · revoke or refund</p>
        </div>
        <button onClick={() => { load(); loadActive(); }} data-testid="bp-refresh" className="inline-flex items-center gap-2 border border-sniper hover:border-olive font-mono uppercase text-sm px-4 py-2.5 transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Active & Expiring overview — everyone currently on the $5 / 7-day pass */}
      <div className="grid sm:grid-cols-3 gap-3 mt-6" data-testid="bp-summary">
        <SummaryTile label="Active Passes" value={active ? active.active_count : "—"} sub="holding full browse-&-buy access right now" tone="lime" testid="bp-tile-active" />
        <SummaryTile label="Expiring in 24h" value={active ? active.expiring_24h : "—"} sub="no auto-renew — access ends, doesn't downgrade" tone="amber" testid="bp-tile-expiring" />
        <SummaryTile label="Sold All-Time" value={active ? active.sold_all_time : "—"} sub={active ? `${fmtCents(active.gross_cents)} gross · ${active.active_count} currently active` : ""} tone="plain" testid="bp-tile-sold" />
      </div>

      <div className="mt-8 flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-heading text-2xl font-bold uppercase tracking-tight">Active &amp; Expiring</h3>
        <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">sorted by days left, soonest first</span>
      </div>
      <div className="mt-3 border border-sniper" data-testid="bp-active-table">
        <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_0.9fr_auto] gap-3 px-4 py-2.5 bg-surface font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Buyer</span><span>Purchased</span><span>Expires</span><span>Days Left</span><span>Status</span>
        </div>
        {!active ? (
          <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm p-6" data-testid="bp-active-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : active.passes.length === 0 ? (
          <div className="p-8 text-center text-secondary-foreground font-mono text-sm" data-testid="bp-active-empty">No active Buyer Passes right now.</div>
        ) : (
          active.passes.map((p) => {
            const pct = Math.max(4, Math.min(100, Math.round((p.days_left / (active.pass_days || 7)) * 100)));
            const barColor = p.expiring_soon ? "#E0842F" : "#cfe86b";
            const handle = (p.username || p.user_id || "").replace(/^@/, "");
            const initial = (handle || "?").charAt(0).toUpperCase();
            return (
              <div key={p.user_id} className="grid grid-cols-2 sm:grid-cols-[1.5fr_1fr_1fr_0.9fr_auto] gap-2 sm:gap-3 px-4 py-3 border-t border-sniper items-center text-[13px]" data-testid={`bp-active-row-${p.user_id}`}>
                <div className="col-span-2 sm:col-span-1 flex items-center gap-2.5 min-w-0">
                  <span className="w-7 h-7 shrink-0 bg-surface border border-sniper flex items-center justify-center font-mono text-[11px] uppercase text-muted-foreground">{initial}</span>
                  <span className="font-heading font-bold truncate">@{handle}</span>
                </div>
                <div className="text-[12px] text-secondary-foreground" data-testid={`bp-active-purchased-${p.user_id}`}>{fmtShort(p.purchased_at)}</div>
                <div className="text-[12px] text-secondary-foreground" data-testid={`bp-active-expires-${p.user_id}`}>{fmtShort(p.expires_at)}</div>
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-bold tabular-nums" style={{ color: barColor }} data-testid={`bp-active-daysleft-${p.user_id}`}>{p.days_left}</span>
                  <span className="hidden sm:block h-1.5 w-[64px] bg-white/10 overflow-hidden"><span className="block h-full" style={{ width: `${pct}%`, background: barColor }} /></span>
                </div>
                <div>
                  {p.expiring_soon
                    ? <span className="inline-block font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 border" style={{ color: "#E0842F", borderColor: "#E0842F", background: "rgba(224,132,47,0.12)" }} data-testid={`bp-active-status-${p.user_id}`}>Expiring Soon</span>
                    : <span className="inline-block font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 border border-olive text-olive-bright bg-olive/15" data-testid={`bp-active-status-${p.user_id}`}>Active</span>}
                </div>
              </div>
            );
          })
        )}
      </div>

      <h3 className="font-heading text-2xl font-bold uppercase tracking-tight mt-10">All Purchases</h3>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-5 flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search handle, email or purchase id"
            data-testid="bp-search-input"
            className="w-full bg-background border border-sniper pl-9 pr-3 py-2.5 text-[13px] text-foreground font-mono focus:outline-none focus:border-olive" />
        </div>
        <button type="submit" data-testid="bp-search-btn" className="border border-sniper hover:border-olive font-mono uppercase text-xs px-4 transition-colors">Search</button>
      </form>

      {!superAdmin && (
        <p className="mt-4 text-[12px] font-mono uppercase text-volt" data-testid="bp-super-note">Refunds move money — super-admin only. You can revoke access.</p>
      )}

      <div className="mt-5 border border-sniper" data-testid="bp-table">
        <div className="hidden sm:grid grid-cols-[1.4fr_0.7fr_0.7fr_1fr_1fr_auto] gap-3 px-4 py-2.5 bg-surface font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>Buyer</span><span>Amount</span><span>Status</span><span>Purchased</span><span>Pass</span><span className="text-right">Actions</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm p-6" data-testid="bp-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-secondary-foreground font-mono text-sm" data-testid="bp-empty">No Buyer Pass purchases yet.</div>
        ) : (
          rows.map((r) => (
            <div key={r.purchase_id} className="grid grid-cols-2 sm:grid-cols-[1.4fr_0.7fr_0.7fr_1fr_1fr_auto] gap-2 sm:gap-3 px-4 py-3 border-t border-sniper items-center text-[13px]" data-testid={`bp-row-${r.purchase_id}`}>
              <div className="col-span-2 sm:col-span-1 min-w-0">
                <div className="font-heading font-bold truncate">{r.username ? `@${r.username}` : (r.user_id || "—")}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.email || r.purchase_id}</div>
              </div>
              <div className="tabular-nums">{fmtCents(r.amount_cents)}</div>
              <div><StatusChip row={r} /></div>
              <div className="text-[11px] text-secondary-foreground">{fmtDate(r.created_at)}</div>
              <div className="text-[11px]">
                {r.pass_active
                  ? <span className="text-olive-bright" data-testid={`bp-passactive-${r.purchase_id}`}>Active · {fmtDate(r.pass_expires_at)}</span>
                  : <span className="text-muted-foreground">Inactive</span>}
              </div>
              <div className="col-span-2 sm:col-span-1 flex gap-2 sm:justify-end flex-wrap">
                {r.pass_active && (
                  <button onClick={() => setAction({ mode: "revoke", row: r })} data-testid={`bp-revoke-${r.purchase_id}`}
                    className="inline-flex items-center gap-1 border border-sniper hover:border-olive font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                    <Ban className="w-3 h-3" /> Revoke
                  </button>
                )}
                {r.status === "completed" && superAdmin && (
                  <button onClick={() => setAction({ mode: "refund", row: r })} data-testid={`bp-refund-${r.purchase_id}`}
                    className="inline-flex items-center gap-1 border border-blaze text-blaze hover:bg-blaze/15 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                    <RotateCcw className="w-3 h-3" /> Refund
                  </button>
                )}
                {r.status === "refunded" && <span className="font-mono text-[10px] uppercase text-muted-foreground">Refunded {fmtDate(r.refunded_at)}</span>}
              </div>
            </div>
          ))
        )}
      </div>

      {action && (
        <ActionModal mode={action.mode} row={action.row} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); loadActive(); }} />
      )}
    </div>
  );
}
